import React from "react";
import { Alert } from "react-native";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import { BasicInfoStackScreenProps } from "../BasicInfoNavigator";
import {
  BaseCompleteScreen,
  navigateToNextSection,
  getNextSectionButtonLabel,
} from "../../components/BaseCompleteScreen";
import { buildQuickExposureLifestylePatch } from "../quickExposure";

export function AboutYouComplete({
  navigation,
}: BasicInfoStackScreenProps<"AboutYouComplete">) {
  const t = useTranslations();
  const { state } = useBasicInfo("AboutYouComplete");
  const { data: patient } = usePatient();
  const { mutateAsync: updatePatient, isPending } = useQueuedPatientUpdate();

  const handleSubmit = async () => {
    const {
      dateOfBirth,
      height,
      weight,
      hasBeenPregnant,
      hasEndometriosis,
      suspectPCOS,
      pcosDiagnosed,
      hadVaricoceles,
      hasCancerHistory,
      hasAllergies,
      allergies,
      sex,
      pregnancyCount,
      quickExposureSubstances,
    } = state;

    try {
      let pregnancies: { id?: string; op?: "delete" }[] =
        patient?.obGynHistory?.pregnancies?.map((p) => ({ id: p.id })) ?? [];

      if (hasBeenPregnant === false) {
        pregnancies = pregnancies.map((p) => ({
          id: p.id,
          op: "delete" as const,
        }));
      } else if (pregnancyCount) {
        if (pregnancyCount > pregnancies.length) {
          pregnancies = [
            ...pregnancies,
            ...Array.from(
              { length: pregnancyCount - pregnancies.length },
              () => ({}),
            ),
          ];
        } else if (pregnancyCount < pregnancies.length) {
          pregnancies = [
            ...pregnancies.slice(0, pregnancyCount),
            ...pregnancies
              .slice(pregnancyCount)
              .map((p) => ({ id: p.id, op: "delete" as const })),
          ];
        }
      }

      await updatePatient({
        dateOfBirth: dateOfBirth ?? undefined,
        gender: sex ?? undefined,
        bodyData: {
          id: patient?.bodyData?.id,
          height: height ?? undefined,
          weight: weight ?? undefined,
        },
        obGynHistory: {
          id: patient?.obGynHistory?.id,
          hasBeenPregnant: hasBeenPregnant ?? undefined,
          pregnancies:
            hasBeenPregnant === false || pregnancyCount
              ? pregnancies
              : undefined,
        },
        medicalHistory: {
          id: patient?.medicalHistory?.id,
          hasEndometriosis: hasEndometriosis ?? undefined,
          suspectPCOS: suspectPCOS ?? undefined,
          hadVaricoceles: hadVaricoceles ?? undefined,
          hasCancerHistory: hasCancerHistory ?? undefined,
          hasAllergies: hasAllergies ?? undefined,
          allergies: (() => {
            if (hasAllergies === false) {
              const existingAllergies =
                patient?.medicalHistory?.allergies ?? [];
              return existingAllergies
                .filter((a) => a.id)
                .map((a) => ({ id: a.id, op: "delete" as const }));
            }
            return Array.isArray(allergies) ? allergies : [];
          })(),
          diseases: (() => {
            const existingPCOSDisease = patient?.medicalHistory?.diseases?.find(
              (d) => d.disease === "pcos",
            );

            if (pcosDiagnosed === true) {
              if (existingPCOSDisease) {
                return [{ id: existingPCOSDisease.id }];
              } else {
                return [
                  {
                    category: "endocrine" as const,
                    disease: "pcos" as const,
                  },
                ];
              }
            } else if (existingPCOSDisease) {
              return [{ id: existingPCOSDisease.id, op: "delete" as const }];
            }
            return undefined;
          })(),
        },
        lifestyle:
          quickExposureSubstances != null
            ? buildQuickExposureLifestylePatch(patient, quickExposureSubstances)
            : undefined,
        basicInfoCompleted: true,
      });

      navigateToNextSection(navigation, "basicInfo");
    } catch {
      Alert.alert(
        t("med_history.basic_info.complete.save_failed_title"),
        t("med_history.basic_info.complete.save_failed_message"),
        [{ text: t("med_history.basic_info.complete.save_failed_ok") }],
      );
    }
  };

  return (
    <BaseCompleteScreen
      title={t("med_history.basic_info.complete.title")}
      subtitle={t("med_history.basic_info.complete.subtitle")}
      secondarySubtitle={null}
      buttonLabel={getNextSectionButtonLabel(t, "basicInfo")}
      submittingLabel={t("med_history.basic_info.complete.saving")}
      isSubmitting={isPending}
      onFinish={handleSubmit}
    />
  );
}

export default AboutYouComplete;
